/**
 * What a visitor sees when a terminal note carries no text (#153).
 *
 * `EmptyReplyReason` landed on the wire in #151 and nothing rendered it,
 * so all three histories still painted the same blank: a whitespace note
 * nobody was asked about, a spoken turn the recognizer could not resolve,
 * and **the model being asked at the demo's payoff and returning
 * nothing**. The third is a defect and the first is a no-op, and until
 * this module existed the surface could not tell a visitor which had
 * happened.
 *
 * ## Text wins, and that is the whole safety property
 *
 * `treatmentFor` reads `text` before it reads `empty`, and a frame with
 * any visible character gets no treatment at all regardless of what the
 * marker says. That ordering is the structural form of the bar this work
 * had to clear: a UI that flagged "Mm." as a failure would fire on the
 * demo's most characterful turns, and would be worse than the blank it
 * replaced. Written as a rule beside a render site it would protect one
 * render site; written as the first line of the only function that
 * classifies, it protects every caller (`specification-driven.md`
 * Pattern 7).
 *
 * ## The absent key
 *
 * The gateway serializes with `exclude_none`, so the key is **absent**
 * when there is text, never `null`. `empty === null` is therefore never
 * true on a real frame, and a check written that way classifies every
 * blank as unmarked while looking correct. `undefined` means "she
 * replied".
 */

import type { EmptyReplyReason } from "@/api/types";

/**
 * How a blank terminal frame is rendered, if at all.
 *
 * - `silent` — render nothing. Nobody asked, nothing ran, nothing was
 *   spent, and a visitor who sent whitespace does not need telling off.
 * - `unheard` — the microphone, not the character. Its own register in
 *   the conversation for that reason: attributing "nothing came through"
 *   to a person makes the character answer for the visitor's equipment.
 * - `no-reply` — the character was asked and the line did not come.
 */
export type EmptyTreatment = "silent" | "unheard" | "no-reply";

/**
 * Classify one terminal note.
 *
 * Returns `null` for anything that should render as an ordinary reply,
 * which includes every frame carrying text.
 *
 * An unrecognised reason is treated as `no-reply` rather than as an
 * ordinary blank. The presence of the key is itself the claim that this
 * blank has a cause; both reasons that are *ordinary* are enumerated
 * here, so a value that is neither is some kind of failure, and painting
 * it as an empty line is the defect this module exists to close.
 */
export function treatmentFor(
  text: string,
  empty: EmptyReplyReason | undefined,
): EmptyTreatment | null {
  // Before anything else. See the header: this ordering is the guard.
  if (text.trim().length > 0) return null;
  if (empty === undefined) return null;
  if (empty === "not_asked") return "silent";
  if (empty === "not_heard") return "unheard";
  return "no-reply";
}
